import { Box, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SortDescIcon } from '~/assets/icons';
import { memo, useEffect, useState } from 'react';
import { SortDirection } from '~/utils/types/job';
import { HeadCelType } from '~/utils/types/common';
import { BaseInput } from '..';
import { SortDirectionEnum } from '~/utils/enum';
import { useTranslation } from 'react-i18next';
import useDebounce from '~/hooks/useDebounce';
import { SEARCH_TIMEOUT } from '~/utils/constants/common';

type BaseTableHeadProps = {
  headCells: Array<HeadCelType>;
  searchInput?: string;
  sortBy?: string;
  sortOrder?: SortDirection;
  onRequestSort: (property: string, sortDirection: SortDirection) => void;
  onRequestSearch: (searchBy: string, value: string) => void;
};

const BaseTableHead = (props: BaseTableHeadProps) => {
  const { t } = useTranslation('admin/common');

  const { headCells, searchInput, sortBy, sortOrder, onRequestSort, onRequestSearch }: BaseTableHeadProps = props;

  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});

  const searchInputDebounce = useDebounce(selectedProperty ? searchInputs[selectedProperty] : '', SEARCH_TIMEOUT);

  useEffect(() => {
    if (!selectedProperty) return;

    const value = searchInputDebounce?.trim() ?? '';
    onRequestSearch(selectedProperty, value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputDebounce, selectedProperty]);

  useEffect(() => {
    if (!searchInput) {
      setSearchInputs({});
      setSelectedProperty('');
    }
  }, [searchInput]);

  const createSortHandler = (property: string) => () => {
    let direction: SortDirection;

    if (sortBy === property) {
      direction = sortOrder === SortDirectionEnum.ASC ? SortDirectionEnum.DESC : SortDirectionEnum.ASC;
    } else {
      direction = SortDirectionEnum.ASC;
    }

    onRequestSort(property, direction);
  };

  const handleSearch = (property: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSelectedProperty(property);
    setSearchInputs((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: HeadCelType, index: number) => (
          <TableCell key={`${headCell.id as string}-${index}`} className={`cursor-pointer ${headCell.className}`}>
            <Box className='flex justify-center items-center'>
              <Typography className='text-sm font-semibold text-[#161A1F]'>{headCell.label ?? headCell.id}</Typography>
              {headCell.sortable && (
                <SortDescIcon
                  onClick={createSortHandler(headCell.id)}
                  className={`w-5 h-5 ml-2 transition ${
                    sortBy === headCell.id ? (sortOrder === SortDirectionEnum.DESC ? 'rotate-180' : '') : 'rotate-180'
                  }`}
                />
              )}
            </Box>
            {headCell.searchable && (
              <BaseInput
                autoComplete='off'
                placeholder={t('search')}
                className='header-search-input'
                value={searchInputs[headCell.id] || ''}
                onChange={handleSearch(headCell.id)}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default memo(BaseTableHead);
